/**
 * Media Planning & Buying services overview page
 * Updated any blue accents to orange and ensured outline buttons include bg-transparent
 */
import { Link } from 'react-router'
import { Button } from '../../components/ui/button'
import { Target, Tv, Radio, FileText, Eye, Mic, ArrowRight } from 'lucide-react'

export default function MediaPlanningPage() {
  return (
    <div>
      {/* Hero Section (kept orange) */}
      <section className="bg-gradient-to-br from-orange-900 via-orange-800 to-red-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">Media Planning & Buying</h1>
              <p className="text-xl text-orange-100 leading-relaxed mb-8">
                Strategic media planning and buying across traditional channels to maximize your brand's 
                reach and impact with precision targeting and optimal budget allocation.
              </p>
              <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600">
                <Link to="/contact">Plan Your Media Strategy</Link>
              </Button>
            </div>
            <div>
              <img 
                src="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/38a85753-99f3-4826-9078-70a8c7f764cb.jpg" 
                alt="Media Planning Services"
                className="w-full h-80 object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Media Planning Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              At Edicoo, our dedicated media planning service revolves around a careful process of tailoring advertising messages to reach specific audiences effectively. Our approach involves a comprehensive analysis of audiences, channels, and advertisements to ensure the optimal delivery of your message.
              By delving into the intricate details of audience demographics and behaviour, we formulate a targeted plan to optimize communication.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* TV Advertising (switch to orange) */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Tv className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">TV Advertising</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Strategic television campaign planning and buying across national and regional networks.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-orange-200 text-orange-700 hover:bg-orange-50">
                <Link to="/services/tv-advertising">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>

            {/* Out-of-Home Advertising */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Out-of-Home Advertising</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Billboards, transit advertising, and outdoor media placements for maximum visibility.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-green-200 text-green-700 hover:bg-green-50">
                <Link to="/services/ooh-advertising">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>

            {/* Radio Advertising */}
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Radio className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Radio Advertising</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Audio campaigns across radio platforms with targeted demographic reach.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-purple-200 text-purple-700 hover:bg-purple-50">
                <Link to="/services/radio-advertising">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>

            {/* Print Advertising */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <FileText className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Print Advertising</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Newspaper and magazine campaigns with strategic placement and creative design.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-yellow-200 text-yellow-700 hover:bg-yellow-50">
                <Link to="/services/print-advertising">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>

            {/* Monitoring & Tracking */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Eye className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Monitoring & Tracking</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Comprehensive campaign performance analysis and real-time monitoring.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-orange-200 text-orange-700 hover:bg-orange-50">
                <Link to="/services/monitoring">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>

            {/* Dubbing & Adaptation */}
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-8 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="bg-pink-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Mic className="h-8 w-8 text-pink-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Dubbing & Adaptation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Audio-visual localization services for international and regional campaigns.
              </p>
              <Button asChild variant="outline" className="w-full bg-transparent border-pink-200 text-pink-700 hover:bg-pink-50">
                <Link to="/services/dubbing">Learn More <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
